/**
 * Wire envelopes (docs/architecture.md §6), plain JSON on every transport and platform:
 *   request  `{ v, id, type, payload, correlationId? }`
 *   response `{ v, id, ok: true, result, ts, correlationId? }` | `{ v, id, ok: false, error, ts, correlationId? }`
 *   event    `{ v, event, payload, seq, ts }`
 * `id` identifies the request and is the idempotency key of mutations: a retry of a mutation whose reply was lost goes
 * under the same id and gets the same answer without a second execution (for config.idempotency.ttlMs, 10 minutes;
 * auth.refresh: see its RequestMap entry). An id must therefore be unique within the client's device session (the
 * server scopes its cache to the session; sign-in requests to their credentials, or the client address) for at least
 * that long and hard to guess: a random prefix per app start plus a counter, or a UUID. `correlationId` is an optional client
 * tracing id (request-id syntax; over HTTP also the X-Correlation-Id header) that the server echoes in the response
 * and writes to its access and error logs. `ts` is the server time (epoch milliseconds) when the response or event
 * was sent. `seq` numbers the events of one player: strictly increasing on every transport (not necessarily
 * contiguous per connection), the position a client resumes from after a reconnect (`events.resume` / `events.poll`).
 */
import { wellFormedText } from '../json';
import { PROTOCOL_ERROR_REASONS, type ProtocolError, isErrorCode } from './errors';
import { PROTOCOL_VERSION, SUPPORTED_PROTOCOL_VERSIONS } from './version';

export interface RequestEnvelope<T extends string = string, P = unknown> {
  v: number;
  id: string;
  type: T;
  payload: P;
  /** Client tracing id, echoed in the response (REQUEST_ID_PATTERN). */
  correlationId?: string;
}

/** Fields the server adds to every response envelope. */
export interface ResponseMeta {
  /** Server time (epoch ms) when the response was sent; always set by this server. */
  ts?: number;
  /** The request's correlation id (envelope field or X-Correlation-Id header), when it had one. */
  correlationId?: string;
}

export type ResponseEnvelope<R = unknown> =
  | ({ v: number; id: string; ok: true; result: R } & ResponseMeta)
  | ({ v: number; id: string; ok: false; error: ProtocolError } & ResponseMeta);

export interface EventEnvelope<E extends string = string, P = unknown> {
  v: number;
  event: E;
  payload: P;
  /** Per-player sequence number (strictly increasing; the resume position of `events.resume` / `events.poll`). */
  seq?: number;
  /** Server time (epoch ms) when the event was emitted. */
  ts?: number;
}

/** Request ids: 1..64 chars of [A-Za-z0-9._:-]. */
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

/** Correlation ids use the request id syntax. */
export const CORRELATION_ID_PATTERN = REQUEST_ID_PATTERN;

/** A valid correlation id, or undefined for anything else. */
export function correlationIdOf(value: unknown): string | undefined {
  return typeof value === 'string' && CORRELATION_ID_PATTERN.test(value) ? value : undefined;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export type ParsedRequest =
  | { ok: true; envelope: RequestEnvelope }
  /** `correlationId`: the request's valid correlation id, so even a refused request can be traced. */
  | { ok: false; id: string; v: number; error: ProtocolError; correlationId?: string };

/**
 * Structural validation of an incoming request (the payload is validated later per `type`). Refusals carry a stable
 * `details.reason`: BAD_REQUEST 'invalid_envelope' with the offending `field` (none when the request is not an
 * object), VERSION_MISMATCH 'unsupported_protocol' with the `supported` versions.
 */
export function parseRequestEnvelope(raw: unknown): ParsedRequest {
  const fallbackId = isObj(raw) && typeof raw.id === 'string' && REQUEST_ID_PATTERN.test(raw.id) ? raw.id : '';
  const cid = isObj(raw) ? correlationIdOf(raw.correlationId) : undefined;
  const fail = (code: ProtocolError['code'], message: string, details?: unknown): ParsedRequest => ({
    ok: false,
    id: fallbackId,
    v: PROTOCOL_VERSION,
    error: details === undefined ? { code, message } : { code, message, details },
    ...(cid !== undefined ? { correlationId: cid } : {}),
  });
  const invalid = (message: string, field?: string): ParsedRequest =>
    fail('BAD_REQUEST', message, field === undefined ? { reason: PROTOCOL_ERROR_REASONS.invalidEnvelope } : { reason: PROTOCOL_ERROR_REASONS.invalidEnvelope, field });
  if (!isObj(raw)) return invalid('Request must be a JSON object');
  if (typeof raw.v !== 'number' || !Number.isInteger(raw.v)) return invalid('Request field "v" must be an integer', 'v');
  if (!SUPPORTED_PROTOCOL_VERSIONS.includes(raw.v)) {
    return fail('VERSION_MISMATCH', `Protocol version ${raw.v} is not supported`, { reason: PROTOCOL_ERROR_REASONS.unsupportedProtocol, supported: [...SUPPORTED_PROTOCOL_VERSIONS] });
  }
  if (typeof raw.id !== 'string' || !REQUEST_ID_PATTERN.test(raw.id)) return invalid('Request field "id" must match [A-Za-z0-9._:-]{1,64}', 'id');
  if (typeof raw.type !== 'string' || raw.type.length === 0 || raw.type.length > 64) return invalid('Request field "type" must be a non-empty string', 'type');
  const payload = raw.payload === undefined ? {} : raw.payload;
  if (raw.correlationId !== undefined && cid === undefined) return invalid('Request field "correlationId" must match [A-Za-z0-9._:-]{1,64}', 'correlationId');
  for (const k of Object.keys(raw)) {
    if (k !== 'v' && k !== 'id' && k !== 'type' && k !== 'payload' && k !== 'correlationId') {
      // The answer names the field as sent, as well-formed text: a lone surrogate (or half of a pair the cut leaves)
      // would make strict JSON decoders refuse the whole answer.
      const field = wellFormedText(k.slice(0, 64));
      return invalid(`Unknown request field "${field}"`, field);
    }
  }
  const envelope: RequestEnvelope = { v: raw.v, id: raw.id, type: raw.type, payload };
  if (cid !== undefined) envelope.correlationId = cid;
  return { ok: true, envelope };
}

export function isResponseEnvelope(raw: unknown): raw is ResponseEnvelope {
  if (!isObj(raw) || typeof raw.v !== 'number' || typeof raw.id !== 'string' || typeof raw.ok !== 'boolean') return false;
  if (raw.ok) return 'result' in raw;
  return isObj(raw.error) && isErrorCode(raw.error.code) && typeof raw.error.message === 'string';
}

export function isEventEnvelope(raw: unknown): raw is EventEnvelope {
  return isObj(raw) && typeof raw.v === 'number' && typeof raw.event === 'string' && 'payload' in raw && !('id' in raw);
}

/** `meta` without undefined members (ts / correlationId appear only when set). */
function metaFields(meta: ResponseMeta | undefined): ResponseMeta {
  const out: ResponseMeta = {};
  if (meta?.ts !== undefined) out.ts = meta.ts;
  if (meta?.correlationId !== undefined) out.correlationId = meta.correlationId;
  return out;
}

export function okResponse<R>(id: string, result: R, v: number = PROTOCOL_VERSION, meta?: ResponseMeta): ResponseEnvelope<R> {
  return { v, id, ok: true, result, ...metaFields(meta) };
}

export function errorResponse(id: string, error: ProtocolError, v: number = PROTOCOL_VERSION, meta?: ResponseMeta): ResponseEnvelope<never> {
  return { v, id, ok: false, error, ...metaFields(meta) };
}

/** A copy of `response` carrying `meta` (the server stamps ts / correlationId on every response it sends). */
export function withResponseMeta<R>(response: ResponseEnvelope<R>, meta: ResponseMeta): ResponseEnvelope<R> {
  return { ...response, ...metaFields(meta) };
}

export function makeEvent<E extends string, P>(event: E, payload: P, v: number = PROTOCOL_VERSION, meta: { seq?: number; ts?: number } = {}): EventEnvelope<E, P> {
  const env: EventEnvelope<E, P> = { v, event, payload };
  if (meta.seq !== undefined) env.seq = meta.seq;
  if (meta.ts !== undefined) env.ts = meta.ts;
  return env;
}
